import i18n from "../../lang/i18n";

// Pure derivations from the /api/v1/analytics/standard-breakdown/ payload.
// Powers the StandardDetail funnel + heatmap + KPI tiles from real per-teacher
// per-question status (no simulation, no randomness).

// A question the lesson's rubric version never had: the teacher was not asked
// it, so it neither passes nor drops them.
const NOT_ASKED = "not_asked";

// The one step shape the funnel, the heatmap and the KPI tiles all read, so a
// step means the same thing whether it was reduced from teacher rows or read
// off the server's counts. The teacher arrays stay empty for the latter — the
// caller must then not offer the drill-downs that list them.
const makeStep = (
  question,
  index,
  { inputCount, passCount, failCount, notObservedCount, notAskedCount },
  { passing = [], failing = [], notObserved = [], notAsked = [] } = {},
) => {
  // The teachers the question was put to; the rest were carried past it.
  const askedCount = inputCount - notAskedCount;
  return {
    stepIndex: index,
    label: question.text,
    // i18n.t rather than a hook: this is a plain module, not a component.
    // Safe because App.js keys the routed tree on the active language, so a
    // switch UNMOUNTS every consumer — a memo keyed on [breakdown] alone
    // (StandardDetail.js) dies with it and cannot serve a stale label.
    shortLabel:
      question.short_label ||
      question.code ||
      i18n.t("common.questionShort", { order: question.order }),
    pdFocus: question.pd_focus || question.text,
    inputCount,
    passCount,
    failCount,
    notObservedCount,
    notAskedCount,
    askedCount,
    // Whoever stays in the pool — passed or not asked: the funnel bar, the KPI
    // survivors and the drill-down behind the bar.
    carriedCount: passCount + notAskedCount,
    carriedTeachers: [...passing, ...notAsked].map((t) => t.teacher),
    failingTeachers: failing.map((t) => t.teacher),
    notObservedTeachers: notObserved.map((t) => t.teacher),
    // Combined for funnel-connector drill-down ("Did not pass at this step").
    droppedTeachers: [...failing, ...notObserved].map((t) => t.teacher),
    // `weakestIndicatorScore` powers the BottleneckHeatmap cell color + header %.
    // Fail-only — excluding not-observed — so it reflects real teaching weak
    // points: high not-observed with low fail is a coverage gap, not a teaching one.
    // A share of the teachers asked: those not asked cannot dilute it.
    weakestIndicatorScore: askedCount > 0 ? failCount / askedCount : 0,
  };
};

// `teachers` is the breakdown's teacher list; each item is
//   { teacher: { uuid, full_name, ... },
//     answers: { [questionUuid]: "pass" | "fail" | "not_observed" | "not_asked" } }
// `questions` is the standard's ordered question list from the same payload.
//
// The funnel is cumulative: at each step only teachers who passed every
// prior question are in the pool. A teacher who explicitly fails OR who
// wasn't observed on a question drops out for the remaining steps — but
// the two are tracked separately so the heatmap can show real failure
// rates (not coverage gaps) and the drill-down modals can label them
// honestly ("Failed" vs "Not observed"). A teacher not asked a question
// stays in the pool without counting either way (backend `funnel_counts`).
export const buildFunnelSteps = (questions, teachers) => {
  let currentPool = teachers;

  return questions.map((q, index) => {
    const passing = [];
    const failing = [];
    const notObserved = [];
    const notAsked = [];

    currentPool.forEach((t) => {
      const status = t.answers?.[q.uuid];
      if (status === "pass") passing.push(t);
      else if (status === "fail") failing.push(t);
      else if (status === NOT_ASKED) notAsked.push(t);
      else notObserved.push(t);
    });

    const step = makeStep(
      q,
      index,
      {
        inputCount: currentPool.length,
        passCount: passing.length,
        failCount: failing.length,
        notObservedCount: notObserved.length,
        notAskedCount: notAsked.length,
      },
      { passing, failing, notObserved, notAsked },
    );

    currentPool = [...passing, ...notAsked];
    return step;
  });
};

// The same steps, read off the server's cumulative counts instead of reduced
// from teacher rows. A scope spanning >1 school never ships those rows — the
// backend runs this exact chain in analytics/services/standards.py
// `funnel_counts` — and sends `question_counts` in their place, keyed by
// question uuid.
export const funnelStepsFromCounts = (questions, questionCounts = {}) => {
  return questions.map((q, index) => {
    const counts = questionCounts?.[q.uuid] || {};
    return makeStep(q, index, {
      inputCount: counts.input_count || 0,
      passCount: counts.pass_count || 0,
      failCount: counts.fail_count || 0,
      notObservedCount: counts.not_observed_count || 0,
      notAskedCount: counts.not_asked_count || 0,
    });
  });
};

// School KPIs for the standard, derived from the funnel + raw teacher answers.
// `steps` is the output of buildFunnelSteps. The survivors are whoever is
// still in the pool after the last step: passed it, or were not asked it.
export const computeStandardKpi = (steps, teachersTotal) => {
  if (!steps.length) {
    return {
      survivors: 0,
      attritionRate: 0,
      weakestIndicatorLabel: "—",
      weakestIndicatorValue: 0,
    };
  }
  const last = steps[steps.length - 1];
  const survivors = last.carriedCount;
  const attritionRate =
    teachersTotal > 0
      ? Math.round(((teachersTotal - survivors) / teachersTotal) * 100)
      : 0;

  let weakestValue = -1;
  let weakestIndex = -1;
  steps.forEach((s, i) => {
    if (s.weakestIndicatorScore > weakestValue) {
      weakestValue = s.weakestIndicatorScore;
      weakestIndex = i;
    }
  });
  const weakestStep = weakestIndex >= 0 ? steps[weakestIndex] : null;

  return {
    survivors,
    attritionRate,
    weakestIndicatorLabel: weakestStep?.pdFocus || weakestStep?.label || "—",
    weakestIndicatorValue: Math.max(weakestValue, 0),
  };
};

// Per-question YES / NO / NOT_OBSERVED groups for the Teacher Distribution
// panel. Independent of the cumulative funnel: every teacher is counted on
// every question regardless of whether they "dropped out" earlier in the
// funnel chain. A teacher not asked the question is in none of the three.
export const groupTeachersByQuestion = (questions, teachers) =>
  questions.map((q) => {
    const pass = [];
    const fail = [];
    const notObserved = [];
    teachers.forEach((t) => {
      const status = t.answers?.[q.uuid];
      if (status === "pass") pass.push(t.teacher);
      else if (status === "fail") fail.push(t.teacher);
      else if (status !== NOT_ASKED) notObserved.push(t.teacher);
    });
    return { question: q, pass, fail, notObserved };
  });
